import { useRoute, useRouter } from 'vue-router'
import { computed } from 'vue'
import qs from 'qs'
import { defineStore } from 'pinia'
import { compressToEncodedURIComponent, decompressFromEncodedURIComponent } from 'lz-string'

import { maLocalStorage } from '@/lib/storage'
import { getStoreName, safeTrackIdEquals } from '@/base/MaUtils'
import maDate from '@/base/MaDate'
import Store from '@/base/constants/Store'
import Device from '@/base/constants/Device'

export const useUri = defineStore('uri', () => {
  const route = useRoute()
  const router = useRouter()

  const normalizeDate = (value) => {
    const isValid = maDate(value).isValid()
    return isValid ? value : maDate().formatISODate()
  }

  const getRouteClone = () => ({
    name: route.name,
    params: { ...route.params },
    query: { ...route.query },
  })

  const setLocalStorage = (key, value) => {
    maLocalStorage[key] = value
  }

  const setParams = (key, value) => {
    const routeClone = getRouteClone()
    if (routeClone.query[key] === value) {
      return
    }
    routeClone.params[key] = value
    setLocalStorage(key, value)
    router.push(routeClone)
  }

  const setQuery = (key, value) => {
    const routeClone = getRouteClone()
    if (routeClone.query[key] === value) {
      return
    }
    routeClone.query[key] = value
    setLocalStorage(key, value)
    router.push(routeClone)
  }

  const getCompressedQuery = (key) => {
    const compressedItems = qs.parse(route.query)?.[key] || ''
    if (compressedItems) {
      const decompressedItems = decompressFromEncodedURIComponent(compressedItems)
      if (decompressedItems) {
        return JSON.parse(decompressedItems).map(taxonomy => Number(taxonomy))
      }
    }
    return undefined
  }

  const setCompressedQuery = (key, value = []) => {
    setQuery(key, compressToEncodedURIComponent(JSON.stringify(value)))
  }

  const replaceRouteParams = async ({ params, query }) => {
    const routeClone = getRouteClone()
    routeClone.params = params || routeClone.params
    routeClone.query = query || routeClone.query

    const keys = Object.assign({}, params, query)
    for (const key in keys) {
      setLocalStorage(key, keys[key])
    }

    await router.push(routeClone)
  }

  const activeApp = computed({
    get() {
      return qs.parse(route.query)?.activeApp?.toString()
    },
    set(val) {
      setQuery('activeApp', val)
    },
  })

  const trackId = computed({
    get() {
      return qs.parse(route.params)?.trackId
    },
    set(val) {
      setParams('trackId', val)

      // guard against setting activeApp to the same value as trackId
      if (!safeTrackIdEquals(val, activeApp.value) && !route.query.activeApp) {
        maLocalStorage.activeApp = val
      }
    },
  })

  const countryCode = computed({
    get() {
      return qs.parse(route.query)?.countryCode
    },
    set(val) {
      setQuery('countryCode', val)
    },
  })

  const countryCodes = computed({
    get() {
      return qs.parse(route.query)?.countryCodes || []
    },
    set(val) {
      setQuery('countryCodes', val)
    },
  })

  const date = computed({
    get() {
      return normalizeDate(route.query?.date)
    },
    set(val) {
      setQuery('date', val ? maDate(val).formatISODate() : undefined)
    },
  })

  const startDate = computed({
    get() {
      return normalizeDate(route.query?.startDate)
    },
    set(val) {
      setQuery('startDate', val ? maDate(val).formatISODate() : undefined)
    },
  })

  const endDate = computed({
    get() {
      return normalizeDate(route.query?.endDate)
    },
    set(val) {
      setQuery('endDate', val ? maDate(val).formatISODate() : undefined)
    },
  })

  const compareStartDate = computed({
    get() {
      return normalizeDate(route.query?.compareStartDate)
    },
    set(val) {
      setQuery('compareStartDate', val ? maDate(val).formatISODate() : undefined)
    },
  })

  const compareEndDate = computed({
    get() {
      return normalizeDate(route.query?.compareEndDate)
    },
    set(val) {
      setQuery('compareEndDate', val ? maDate(val).formatISODate() : undefined)
    },
  })

  const selectedDate = computed({
    get() {
      return normalizeDate(route.query?.selectedDate)
    },
    set(val) {
      setQuery('selectedDate', val ? maDate(val).formatISODate() : undefined)
    },
  })

  const publisherId = computed({
    get() {
      return qs.parse(route.params)?.publisherId
    },
    set(val) {
      setParams('publisherId', val)
    },
  })

  const developerName = computed({
    get() {
      return qs.parse(route.params)?.developerName
    },
    set(val) {
      setParams('developerName', val)
    },
  })

  const platformId = computed({
    get() {
      return qs.parse(route.query)?.platformId
    },
    set(val) {
      setQuery('platformId', val)
    },
  })

  const name = computed({
    get() {
      return qs.parse(route.query)?.name
    },
    set(val) {
      setQuery('name', val)
    },
  })

  const store = computed({
    get() {
      return qs.parse(route.query)?.store
    },
    set(val) {
      setQuery('store', val)
    },
  })

  const device = computed({
    get() {
      if (route.params?.trackId && getStoreName(route.params?.trackId) === Store.Ios && route.query?.device === Device.Android)
        return Device.Iphone

      return qs.parse(route.query)?.device
    },
    set(val) {
      setQuery('device', val)
    },
  })

  const categoryId = computed({
    get() {
      return qs.parse(route.query)?.categoryId?.toString()
    },
    set(val) {
      setQuery('categoryId', val)
    },
  })

  const rankingType = computed({
    get() {
      return qs.parse(route.query)?.rankingType
    },
    set(val) {
      setQuery('rankingType', val)
    },
  })

  const rankingFrequency = computed({
    get() {
      return qs.parse(route.query)?.rankingFrequency
    },
    set(val) {
      setQuery('rankingFrequency', val)
    },
  })

  const ageId = computed({
    get() {
      return qs.parse(route.query)?.ageId?.toString()
    },
    set(val) {
      setQuery('ageId', val)
    },
  })

  const sortType = computed({
    get() {
      return qs.parse(route.query)?.sortType
    },
    set(val) {
      setQuery('sortType', val)
    },
  })

  const chartScale = computed({
    get() {
      return qs.parse(route.query)?.chartScale
    },
    set(val) {
      setQuery('chartScale', val)
    },
  })

  const chartDataMode = computed({
    get() {
      return qs.parse(route.query)?.chartDataMode
    },
    set(val) {
      setQuery('chartDataMode', val)
    },
  })

  const languageCode = computed({
    get() {
      return qs.parse(route.query)?.languageCode
    },
    set(val) {
      setQuery('languageCode', val)
    },
  })

  const categories = computed({
    get() {
      return qs.parse(route.query)?.categories || []
    },
    set(val) {
      setQuery('categories', val)
    },
  })

  const keyword = computed({
    get() {
      const _keyword = qs.parse(route.query)?.keyword
      return typeof _keyword === 'string' ? decodeURI(_keyword) : undefined
    },
    set(val) {
      setQuery('keyword', val)
    },
  })

  /**
   * For some endpoints, having the keyword in lowercase does not make a difference,
   * while for others it does. This computed value is used to store the lowercase version of the keyword.
   */
  const keywordLowerCase = computed(() => keyword.value?.toLowerCase())

  const keywords = computed({
    get() {
      const keywordsFromQuery = qs.parse(route.query)?.keywords || []

      if (Array.isArray(keywordsFromQuery)) {
        return keywordsFromQuery
      }
      else if (typeof keywordsFromQuery === 'string') {
        return keywordsFromQuery.split(',').map(keyword => keyword.trim())
      }
      else {
        return []
      }
    },
    set(val) {
      setQuery('keywords', val)
    },
  })

  /**
   * For some endpoints, having the keywords in lowercase does not make a difference,
   * while for others it does. This computed value is used to store the lowercase version of the keywords
   */
  const keywordsLowerCase = computed(() => keywords.value.map(keyword => keyword.toLowerCase()))

  const cppFilter = computed({
    get() {
      return qs.parse(route.query)?.cppFilter
    },
    set(val) {
      setQuery('cppFilter', val)
    },
  })

  const month = computed({
    get() {
      return qs.parse(route.query)?.month
    },
    set(val) {
      setQuery('month', val)
    },
  })

  const year = computed({
    get() {
      return qs.parse(route.query)?.year
    },
    set(val) {
      setQuery('year', val)
    },
  })

  const appUpdateTypes = computed({
    get() {
      const parsedValue = qs.parse(route.query, { comma: true })?.appUpdateTypes || []
      return Array.isArray(parsedValue) ? parsedValue : [parsedValue]
    },
    set(val) {
      setQuery('appUpdateTypes', val)
    },
  })

  const progression = computed({
    get() {
      return qs.parse(route.query)?.progression
    },
    set(val) {
      setQuery('progression', val)
    },
  })

  const categoryType = computed({
    get() {
      return qs.parse(route.query)?.categoryType
    },
    set(val) {
      setQuery('categoryType', val)
    },
  })

  const videoFilter = computed({
    get() {
      return qs.parse(route.query)?.videoFilter
    },
    set(val) {
      setQuery('videoFilter', val)
    },
  })

  const adNetwork = computed({
    get() {
      return qs.parse(route.query)?.adNetwork
    },
    set(val) {
      setQuery('adNetwork', val)
    },
  })

  const countryFilter = computed({
    get() {
      return qs.parse(route.query)?.countryFilter
    },
    set(val) {
      setQuery('countryFilter', val)
    },
  })

  const appTaxonomies = computed({
    get() {
      return getCompressedQuery('appTaxonomies') || []
    },
    set(val) {
      setCompressedQuery('appTaxonomies', val)
    },
  })

  const gameTaxonomies = computed({
    get() {
      return getCompressedQuery('gameTaxonomies') || []
    },
    set(val = []) {
      setCompressedQuery('gameTaxonomies', val)
    },
  })

  const $reset = () => {}

  return {
    trackId,
    countryCode,
    cppFilter,
    date,
    startDate,
    endDate,
    selectedDate,
    activeApp,
    publisherId,
    name,
    store,
    device,
    categoryId,
    rankingType,
    rankingFrequency,
    ageId,
    countryCodes,
    sortType,
    chartScale,
    chartDataMode,
    languageCode,
    categories,
    keyword,
    keywordLowerCase,
    keywords,
    keywordsLowerCase,
    month,
    year,
    appUpdateTypes,
    progression,
    categoryType,
    videoFilter,
    developerName,
    platformId,
    adNetwork,
    countryFilter,
    appTaxonomies,
    gameTaxonomies,
    compareStartDate,
    compareEndDate,
    replaceRouteParams,
    $reset,
  }
})
